// owner-map: REG-4
// ── Forecaster registry (from /health's first-class forecaster surface) ──
// /health returns `forecasters: [{name, display_name, tag?}]` — `name` is the
// orchestrator value requests send, `tag` ("quick"/"deep") marks the per-tier
// customer default, and `display_name` is the server-side label. The registry is a mutable
// module object populated by the health callback.
export const FORECASTER_INFO = {}; // name -> {display_name, tag|null}
export const applyForecasters = (registry, forecasters) => {
  Object.keys(registry).forEach((k) => delete registry[k]);
  (forecasters || []).forEach((f) => {
    registry[f.name] = { display_name: f.display_name || f.name, tag: f.tag || null };
  });
};
// The forecaster registry is persisted from the last /health result and hydrated
// synchronously at module load, so display names resolve before the /health round-trip
// lands. Otherwise forecasterDisplayName falls back to the raw orchestrator id, which never
// self-heals for a stuck/done leg with no further progress tick. /health refreshes the
// registry and this cache on every load.
export const FORECASTER_CACHE_KEY = "sooth-forecaster-registry";
export const _toForecasterList = (registry) =>
  Object.keys(registry).map((name) => ({
    name,
    display_name: registry[name].display_name,
    tag: registry[name].tag,
  }));
export const persistForecasterCache = () => {
  try {
    localStorage.setItem(
      FORECASTER_CACHE_KEY,
      JSON.stringify({ cloud: _toForecasterList(FORECASTER_INFO) }),
    );
  } catch (_e) {
    /* quota / private mode — skip */
  }
};
try {
  const cached = JSON.parse(localStorage.getItem(FORECASTER_CACHE_KEY) || "null");
  if (cached) applyForecasters(FORECASTER_INFO, cached.cloud);
} catch (_e) {
  /* corrupt/absent/no-localStorage — /health will populate */
}
export const forecasterInfo = (e) => (e && FORECASTER_INFO[e]) || null;

// ── Mirabilis setting (debug Settings ▸ Engines) ──
//
// `sayer-quick-tier-lane` / `sayer-deep-tier-lane` move the /health `tag` onto a
// Mirabilis lane wherever those flags target. The tag is a DEPLOYMENT decision and
// every browser follows it by default; this setting is a per-browser opt-out layered
// on top. OFF resolves the tier to the forecaster that owned it before the cutover.
//
// Keyed off the tag's LIVE owner rather than off the environment, because restating the
// flags' targeting rule here would let the two silently disagree.
// Deep Mirabilis may own the deep tier under any of these names, and stored forecasts keep
// the name they ran under, so all of them mean Mirabilis wherever the tier owner is read.
// Oldest first: `retiredEngineTag` reads an earlier entry as superseded by a later owner.
const MIRABILIS_DEEP_OWNERS = Object.freeze([
  "sooth_mirabilis_sayer",
  "sooth_mirabilis_v3",
  "sooth_mirabilis_v4",
  "sooth_mirabilis_v4_1",
]);
// The reviewed quick-tier alternates, oldest first, read the same way as
// MIRABILIS_DEEP_OWNERS by `retiredEngineTag`. Keep in step with SAYER_QUICK_TIER_ALTERNATES
// in server/flags/sayerQuickTierLane.js.
const QUICK_ALTERNATE_OWNERS = Object.freeze(["sooth_mirabilis_quick", "gemini_3_8_flash_elab"]);
// Successive owners per tier: an earlier one reads as retired once /health tags a later one.
const OWNER_SUCCESSION = Object.freeze({
  deep: MIRABILIS_DEEP_OWNERS,
  quick: QUICK_ALTERNATE_OWNERS,
});
// The lane each Mirabilis owner replaced, which a browser that turned the setting off runs.
const MIRABILIS_FALLBACK_LANE = {
  sooth_mirabilis_quick: "gemini_3_6_flash",
  gemini_3_8_flash_elab: "gemini_3_6_flash",
  ...Object.fromEntries(MIRABILIS_DEEP_OWNERS.map((lane) => [lane, "sooth_forecast_engine_v0_1"])),
};
const MIRABILIS_LANES_KEY = "sooth-mirabilisLanes";
let mirabilisLanesOn = true;
try {
  mirabilisLanesOn = localStorage.getItem(MIRABILIS_LANES_KEY) !== "false";
} catch (_e) {
  /* private mode / no localStorage — keep the default */
}
export const mirabilisLanesEnabled = () => mirabilisLanesOn;
/**
 * Module state, not React state, because `tagOwner`/`engineTag` are pure reads called from
 * render. The caller updates this FIRST and then re-renders, so a flip is never one paint
 * behind.
 */
export const setMirabilisLanesEnabled = (on) => {
  mirabilisLanesOn = !!on;
  try {
    localStorage.setItem(MIRABILIS_LANES_KEY, mirabilisLanesOn ? "true" : "false");
  } catch (_e) {
    /* quota / private mode — the setting lasts the session only */
  }
};
/**
 * Gates the Settings row: true only where the toggle would do something, so the row stays
 * hidden where it would be inert.
 */
export const mirabilisLanesAvailable = () =>
  Object.entries(MIRABILIS_FALLBACK_LANE).some(
    ([lane, prev]) => FORECASTER_INFO[lane]?.tag && FORECASTER_INFO[prev],
  );
// Where the cutover has not reached, this is exactly the tagged owner, so the setting
// changes nothing.
// `on` defaults to the live setting; callers pass it explicitly to ask what a
// tier WOULD resolve to under the other state, without touching the real one.
const effectiveOwner = (tag, reg, on = mirabilisLanesOn) => {
  const tagged = Object.keys(reg).find((k) => reg[k].tag === tag);
  if (!tagged || on) return tagged || null;
  const fallback = MIRABILIS_FALLBACK_LANE[tagged];
  return fallback && reg[fallback] ? fallback : tagged;
};
// The WIRE tag, exactly as /health published it. The narrowest of the three tier
// reads below and the one to reach for when the question is what the server said.
export const engineTag = (e) => forecasterInfo(e)?.tag || null;
/**
 * The tier an engine serves FOR THIS USER, or null — the SELECTOR's question
 * ("which segment is lit", "what does the tier button run"). Reads the effective
 * owner rather than the wire tag, so the opted-out predecessor reads as its tier
 * and the tagged-but-superseded Mirabilis lane reads as none: exactly one engine
 * claims a tier in either state, which the segmented control relies on.
 *
 * NOT for classifying a run that already happened — a stored Mirabilis result is
 * still a Mirabilis result after its author opts out. Use `laneTier` there.
 */
export const effectiveTier = (e) => {
  if (!e || !FORECASTER_INFO[e]) return null;
  for (const tier of TIER_ORDER) if (effectiveOwner(tier, FORECASTER_INFO) === e) return tier;
  return null;
};
/**
 * The tier a lane belongs to at all, whoever picked it, for classifying a RUN where the
 * answer must not swing with a Settings toggle.
 *
 * Each of the three reads only ever maps a name to its OWN tier, so their union cannot
 * misassign; it can only find an answer where a narrower read has none.
 * `LEGACY_ENGINE_TAGS` is setting-independent, which keeps a stored predecessor run
 * classified after its author opts IN. Without it a `gemini_3_6_flash` run would read as
 * tierless the moment Mirabilis took the quick tag, climb the deep ladder, and, for
 * `sooth_forecast_engine_v0_1`, stop asking for the deep-spend confirmation.
 *
 * That read also answers before the registry hydrates, where the others cannot. For a
 * spend confirmation that errs toward asking, which is the safe direction;
 * `engineTierKnown` remains the explicit not-yet-hydrated check.
 */
export const laneTier = (e) => {
  if (!e) return null;
  return engineTag(e) || effectiveTier(e) || retiredEngineTag(e) || null;
};
export const tagOwner = (tag, { mirabilis = mirabilisLanesOn } = {}) =>
  effectiveOwner(tag, FORECASTER_INFO, mirabilis);
/**
 * The engine to select after flipping the Mirabilis setting to `on` — the lane
 * that will serve the tier the user is on once the flip takes effect — or null
 * to keep the current selection (no engine, or one serving no tier).
 *
 * The tier is read as the engine served it under the OTHER setting (`!on`), never the live
 * one, so the answer is the same before or after the flip. That lets a caller apply it in a
 * state updater, against whatever engine is current when it runs (the page-load reset to
 * Quick included), rather than against one captured when the flip was asked for.
 */
export const engineAfterMirabilisFlip = (engine, on) => {
  if (!engine || !FORECASTER_INFO[engine]) return null;
  const tier = TIER_ORDER.find((t) => effectiveOwner(t, FORECASTER_INFO, !on) === engine);
  if (!tier) return null;
  return tagOwner(tier, { mirabilis: !!on });
};
// Customer-facing tier button labels — keyed by tag, FIXED strings. Do NOT
// swap these for /health display_name: re-pointing a tag to a different
// forecaster must not change what the customer buttons say.
export const TIER_LABELS = { quick: "Quick", deep: "Deep" };
// Single source shared by the chat-box Quick/Deep pill and the Settings segmented control,
// so the two never drift.
export const TIER_SHORT = { quick: "Quick", deep: "Deep" };
// "Quick" / "Deep" for the engine that produced a forecast, or null when its tier is not
// known (an untagged lane, or a registry not yet hydrated) — so a caller shows nothing
// rather than guessing. A stored legacy lane reports the tier it served (laneTier reads
// LEGACY_ENGINE_TAGS); an unknown retired lane is NOT re-pointed to the current Quick
// owner the way the request path does, since that would misreport a past vintage.
export const tierShortLabel = (engine) => {
  const tier = engine ? laneTier(engine) : null;
  return (tier && TIER_SHORT[tier]) || null;
};
// `desc` and `switchMessage` are written out together per tier to keep the duration they
// quote in step. Plain text: chat renders markdown, so a `**bold**` tier name here would
// have to be styled rather than spelled.
//
// Deep's duration depends on WHICH forecaster owns the tier, not on the deployment space.
// It is keyed off the tag's live owner rather than restating the `sayer-deep-tier-lane`
// flag's targeting rule here, where the two could silently disagree.
const DEEP_COPY = {
  mirabilis: {
    desc: "Most thorough, up to an hour",
    duration: "up to an hour",
    switchMessage:
      "Depth set to Deep: Sayer's most thorough forecasts. These can take up to an hour.",
  },
  standard: {
    desc: "Most thorough, under 10 minutes",
    duration: "up to 10 minutes",
    switchMessage:
      "Depth set to Deep: Sayer's most thorough forecasts. These take under 10 minutes.",
  },
};
const QUICK_COPY = {
  desc: "Fast, under 2 minutes",
  duration: "under 2 minutes",
  switchMessage: "Depth set to Quick: fast forecasts in under 2 minutes.",
};
// The tier reads as `standard` whenever the registry has no owner for `deep` (a cold
// first load, or no deep-tagged engine), which is also the right answer on every plane
// Mirabilis has not reached.
// An untagged engine gets NO copy rather than a tier's: the Settings control
// falls back to listing every engine when nothing carries a primary tag, so a
// catch-all here would pin a latency claim on a legacy or debug forecaster.
const tierCopy = (tier) => {
  if (tier === "quick") return QUICK_COPY;
  if (tier !== "deep") return null;
  return MIRABILIS_DEEP_OWNERS.includes(tagOwner("deep"))
    ? DEEP_COPY.mirabilis
    : DEEP_COPY.standard;
};
export const tierDesc = (tier) => tierCopy(tier)?.desc ?? null;
export const tierSwitchMessage = (tier) => tierCopy(tier)?.switchMessage ?? null;
/** How long a run at this depth takes, as a phrase ("up to an hour"), or null. */
export const tierDuration = (tier) => tierCopy(tier)?.duration ?? null;
export const TIER_ORDER = ["quick", "deep"];
/** An engine's depth when it is one the user picks (`quick` / `deep`), else null (telemetry). */
export const pickedTier = (e) => {
  const t = laneTier(e);
  return TIER_ORDER.includes(t) ? t : null;
};
// Tiers hidden from the cloud selector. When populated, hidden tiers are filtered out of the
// /health forecaster list at the setter sites, and a persisted hidden engine migrates to a
// visible default via the health-callback validation. Debug-mode compare dropdowns
// deliberately IGNORE this set, since compare may select any supported forecaster.
export const HIDDEN_TIERS = new Set();
// Maps each retired orchestrator name (or the current tier's PREVIOUS owner) to the tag whose
// owner replaces it. Old forecasts persist their engine in `backend`; once a tier is
// re-pointed or a lane is retired, that stored name stops being servable, so forwarding it
// would make the backend 400 "unknown orchestrator". `toServableEngine` re-points it.
export const LEGACY_ENGINE_TAGS = {
  gemini_3_6_flash: "quick",
  sooth_agent_fast: "quick",
  sooth_agent_pro: "deep",
  sooth_agent_quick: "quick",
  sooth_forecast_engine_v0: "deep",
  sooth_forecast_engine_v0_1: "deep",
  sooth_mirabilis_sayer: "deep",
};
/**
 * The tier a stored engine served before it stopped owning one, or null: a
 * LEGACY_ENGINE_TAGS name, or an OWNER_SUCCESSION lane that a LATER lane of the same tier
 * has replaced as the wire owner (deep v3 once /health tags v4; quick Mirabilis once it
 * tags the elaborated Flash lane).
 *
 * The second case is not in LEGACY_ENGINE_TAGS because these lanes are also MAIN research
 * names that /health can list untagged. While no later lane owns the tier, an untagged one
 * is a research or compare pick, not an owner, so it stays tierless; a rollback to an
 * earlier owner leaves the later lane tierless for the same reason.
 */
export const retiredEngineTag = (e) => {
  if (!e) return null;
  if (Object.hasOwn(LEGACY_ENGINE_TAGS, e)) return LEGACY_ENGINE_TAGS[e];
  for (const [tier, owners] of Object.entries(OWNER_SUCCESSION)) {
    const at = owners.indexOf(e);
    if (at < 0) continue;
    const owner = Object.keys(FORECASTER_INFO).find((k) => FORECASTER_INFO[k].tag === tier);
    return owners.indexOf(owner) > at ? tier : null;
  }
  return null;
};
// Map a possibly-retired stored engine to a currently-servable orchestrator, so a request
// never forwards a dead lane. The fallback is the current 'quick' owner; the input is
// returned unchanged when no owner exists.
export const toServableEngine = (engine) => {
  if (!engine) return engine;
  if (!Object.keys(FORECASTER_INFO).length) return engine; // registry not hydrated — don't second-guess
  if (forecasterInfo(engine)) return engine;
  const tag = retiredEngineTag(engine);
  if (tag) {
    const owner = tagOwner(tag);
    if (owner) return owner;
  }
  return tagOwner("quick") || engine;
};
export const isPrimaryEngine = (e) => !!effectiveTier(e);
export const engineLabel = (e) => {
  if (!e) return "Unknown";
  const tag = engineTag(e);
  return tag ? TIER_LABELS[tag] : forecasterInfo(e)?.display_name || e;
};
export const forecasterDisplayName = (e) => {
  if (!e) return "Unknown";
  return forecasterInfo(e)?.display_name || e;
};
// Only for compare-mode debug timeline surfaces; never persisted to chat.
export const forecasterDebugLabel = (e) => {
  if (!e) return "Unknown";
  const dn = forecasterDisplayName(e);
  return dn.toLowerCase() === e.toLowerCase() ? dn : `${dn} (${e})`;
};
// Engine id for a forecast root. `backend` (the raw store-run's engine field) is read first;
// the serverContext/_engine fallbacks cover legacy-shaped forecast objects. Without the
// backend-first read every compare lane resolves null and falls back to the global
// forecastEngine, so both lanes mislabel as the primary.
export const rootEngine = (root) =>
  root?.backend || root?.serverContext?.metadata?.orchestrator || root?._engine || null;
// A report in the full Sayer report format declares it (`report.format`, sayer-report/vN:
// evidence cards, causal chains, scout notes, …). The marker, not the row's engine, decides:
// a pointer can overlay an elaborated report onto a predecessor lane's row, and a failed
// elaboration keeps a plain report, which belongs in the standard layout.
const SAYER_REPORT_FORMAT = /^sayer-report\/v\d+$/;

/**
 * Was this report made by a Mirabilis forecaster, or is it a full Sayer-format report? Read
 * from the run itself — the report's own format marker, else the engine it names, else (no
 * engine, or only a tier alias) its evidence cards, which only Mirabilis runs produce — never
 * from the engine the viewer has picked now, so a stored report keeps the layout it was made
 * for.
 *
 * @param {{engine?: string|null, report?: object|null}} run
 * @returns {boolean}
 */
export const madeWithMirabilis = ({ engine, report }) => {
  if (typeof report?.format === "string" && SAYER_REPORT_FORMAT.test(report.format)) return true;
  if (typeof engine === "string" && engine.trim()) {
    if (/mirabilis/i.test(engine)) return true;
    // A tier alias ("deep", "quick", "engine_deep") names no forecaster, so the cards decide;
    // any other engine named is the forecaster itself.
    const tier = engine
      .trim()
      .toLowerCase()
      .replace(/^engine_/, "");
    if (!Object.hasOwn(ENGINE_ALIASES, tier) && !/^engine_/i.test(engine)) return false;
  }
  return Array.isArray(report?.evidence_cards) && report.evidence_cards.length > 0;
};
// Has /health (or its localStorage cache) told us the tiers yet? A tier can only be read
// against a hydrated registry; before that "not deep" is unknowable, not false — a caller
// gating SPEND on the tier must treat an unhydrated registry as unknown and fail closed,
// not read isDeepEngine's false as a green light.
export const engineTierKnown = () => Object.keys(FORECASTER_INFO).length > 0;
// Does this engine spend at the DEEP tier? Read as `laneTier` after the same retired-name
// re-pointing the request path applies (toServableEngine), so a stored legacy deep lane still
// reads as deep whichever way the user has opted on Mirabilis. Otherwise opting out would
// silently drop the spend confirmation on the one lane still serving the tier. False when
// untagged or falsy; check engineTierKnown first.
export const isDeepEngine = (e) => laneTier(toServableEngine(e)) === "deep";
// Would these two stored engine ids run the SAME orchestrator? Compared after re-pointing,
// because a retired id and the current owner of its tier are one engine on the wire
// (forecastClient re-points before sending) — a raw string compare would call them a
// mismatch and, say, confirm "Deep Forecast … not Deep Forecast". Unhydrated, both pass
// through unchanged, so this degrades to the raw compare.
export const sameServableEngine = (a, b) => toServableEngine(a) === toServableEngine(b);
// Use this wherever a stored engine is shown to a user, so a retired deep lane labels as its
// tier owner ("Deep Forecast"), never as a raw internal lane id.
// Prefers the depth ("Quick"/"Deep"), read before and after re-pointing, so a legacy or
// untagged lane that served a tier never shows its model name to a customer.
export const servableEngineLabel = (e) => {
  const s = toServableEngine(e);
  return tierShortLabel(e) || tierShortLabel(s) || engineLabel(s);
};
// User-friendly aliases the chat can match. Keys are normalized lowercase
// substrings; values are TAGS resolved against the live registry at match
// time, so re-pointing a tag re-points the alias with it.
export const ENGINE_ALIASES = {
  quick: "quick",
  fast: "quick",
  nowcast: "quick",
  deep: "deep",
  pro: "deep",
  thorough: "deep",
  forecast: "deep",
};
// Returns null when no match; the caller should report that to the user.
export const resolveEngine = (input, available) => {
  if (!input || !Array.isArray(available) || !available.length) return null;
  const norm = String(input).toLowerCase().trim();
  for (const eng of available) {
    if (eng.toLowerCase() === norm) return eng;
  }
  for (const eng of available) {
    const labels = [engineLabel(eng), forecasterDisplayName(eng)].map((s) => s.toLowerCase());
    if (labels.includes(norm)) return eng;
  }
  for (const [alias, tag] of Object.entries(ENGINE_ALIASES)) {
    if (norm.includes(alias)) {
      const candidate = tagOwner(tag);
      if (candidate && available.includes(candidate)) return candidate;
    }
  }
  return null;
};
export const sortEngines = (arr) => {
  const idx = (e) => {
    // Effective owner, not the wire tag, so the opted-out predecessor sorts as the tier default.
    const tierIdx = TIER_ORDER.findIndex((t) => effectiveOwner(t, FORECASTER_INFO) === e);
    return tierIdx >= 0
      ? tierIdx
      : TIER_ORDER.length + Math.max(0, Object.keys(FORECASTER_INFO).indexOf(e));
  };
  return [...arr].sort((a, b) => idx(a) - idx(b));
};

// Trigger-LLM (postprocess) options for the dual-engine debug selector. Each leg picks which
// OpenRouter model generates the trigger/events timeline; the default is the forecast
// server's POSTPROCESS_LLM_CONFIG. These are hardcoded rather than server-advertised, since
// "Custom…" already lets a user type any id. Sent to the server as the bare
// `postprocess_model` wire field; the server prepends `openrouter/`.
export const TRIGGER_LLM_OPTIONS = [
  { id: null, label: "Server default" },
  { id: "google/gemini-3.1-pro-preview", label: "Gemini 3.1 Pro" },
  { id: "google/gemini-3.5-flash", label: "Gemini 3.5 Flash" },
  { id: "google/gemini-3-flash-preview", label: "Gemini 3 Flash" },
  { id: "openai/gpt-5.5", label: "GPT 5.5" },
  { id: "x-ai/grok-4.3", label: "Grok 4.3" },
  { id: "anthropic/claude-sonnet-5", label: "Claude Sonnet 5" },
  { id: "anthropic/claude-sonnet-4.6", label: "Claude Sonnet 4.6" },
  { id: "anthropic/claude-opus-4.8", label: "Claude Opus 4.8" },
  { id: "__custom__", label: "Custom…" },
];

const _TRIGGER_LABELS = new Map(
  TRIGGER_LLM_OPTIONS.filter((o) => o.id && o.id !== "__custom__").map((o) => [o.id, o.label]),
);

export function isKnownTriggerLlm(id) {
  return typeof id === "string" && _TRIGGER_LABELS.has(id);
}

export function triggerLabel(id) {
  if (!id) return "Server default";
  return _TRIGGER_LABELS.get(id) || id;
}

/**
 * Coerce a leg's trigger-LLM UI state to the effective wire value (a bare OpenRouter id) or
 * null. The transient "__custom__" sentinel means no override. This is the ONE place the
 * null/custom coercion lives, so the wire guard, the per-leg run stamp and branch inheritance
 * all agree.
 */
export function effectiveTriggerModel(v) {
  return typeof v === "string" && v && v !== "__custom__" ? v : null;
}
